"use client";
import React from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import RetirementStepsTitle from "./RetirementStepsTitle";
import { ChartWithLabel } from "@/type/financialPageTypes";
import {
  getBucketAllocation,
  POST_RETIREMENT_HEALTH_GROWTH_RATE,
  POST_RETIREMENT_PENSION_RATE,
  POST_RETIREMENT_VACATION_RATE,
  PRE_RETIREMENT_LONG_TENURE_RETURN,
  PRE_RETIREMENT_RETURN,
} from "@/utils/financialPageFunctions";
import { useIsMobile } from "@/utils/useIsMobile";
import { useRetirementBuckets } from "../hooks/useRetirementBuckets";
import { fmtINR } from "../ui/RetirementBucketGrid";

const CHIP_STYLES: Record<"fund" | "rate" | "alloc", string> = {
  fund: "bg-[#E7F0FF] text-primaryBlue",
  rate: "bg-[#EDFFE7] text-[#138300]",
  alloc: "bg-[#FFF8E7] text-[#C06600]",
};

export default function PostRetirementScenarioScreen({
  corpus,
  retirementDuration,
}: {
  corpus: number;
  retirementDuration: number;
}) {
  const isMobile = useIsMobile();

  const bucketAllocations = getBucketAllocation(corpus);

  const {
    pensionBucket,
    healthBucket,
    vacationBucket,
    legacyBucket,
    pensionMonthly,
    healthGrows,
    vacationAnnual,
    legacyGrows,
  } = useRetirementBuckets(corpus, retirementDuration, bucketAllocations);

  const legendItems: {
    label: string;
    percent: number;
    dotColor: string;
    value: number;
    secondaryValue: number;
    prefix: React.ReactNode;
    suffix: string;
  }[] = [
    {
      label: "Pension Bucket",
      percent: bucketAllocations.pension,
      dotColor: "#FDE006",
      value: pensionBucket,
      secondaryValue: pensionMonthly,
      prefix: "",
      suffix: "/M",
    },
    {
      label: "Health Reserve",
      percent: bucketAllocations.healthEmergency,
      dotColor: "#FF9304",
      value: healthBucket,
      secondaryValue: healthGrows,
      prefix: "Grows to",
      suffix: "",
    },
    {
      label: "Vacation",
      percent: bucketAllocations.vacation,
      dotColor: "#04BCFF",
      value: vacationBucket,
      secondaryValue: vacationAnnual,
      prefix: "",
      suffix: "/Yr",
    },
    {
      label: "Legacy",
      percent: bucketAllocations.legacy ?? 0,
      dotColor: "#79FD06",
      value: legacyBucket,
      secondaryValue: legacyGrows,
      prefix: "Grows to",
      suffix: "",
    },
  ];

  const details: {
    title: string;
    dotColor: string;
    value: string;
    description: string;
    chips: { label: string; variant: "fund" | "rate" | "alloc" }[];
  }[] = [
    {
      title: "Pension",
      dotColor: "#FDE006",
      value: fmtINR(pensionMonthly) + "/M",
      description: "Stable monthly income covering day-to-day expenses and lifestyle continuity.",
      chips: [
        { label: "Flexi Cap Fund", variant: "fund" },
        { label: `${retirementDuration}Y: ${POST_RETIREMENT_PENSION_RATE}% p.a.`, variant: "rate" },
        { label: `Alloc: ${fmtINR(pensionBucket)}`, variant: "alloc" },
      ],
    },
    {
      title: "Health Reserve",
      dotColor: "#FF9304",
      value: fmtINR(healthGrows),
      description: "Dedicated reserve for medical expenses, hospitalisation and emergencies.",
      chips: [
        { label: "Ultra Short Duration", variant: "fund" },
        { label: `${retirementDuration}Y: ${POST_RETIREMENT_HEALTH_GROWTH_RATE}% p.a.`, variant: "rate" },
        { label: `Alloc: ${fmtINR(healthBucket)}`, variant: "alloc" },
      ],
    },
    {
      title: "Vacation",
      dotColor: "#04BCFF",
      value: `${fmtINR(vacationAnnual)}/Yr`,
      description: "Fund vacations, hobbies and experiences that make your retirement years memorable.",
      chips: [
        { label: "Multi Asset", variant: "fund" },
        { label: `${retirementDuration}Y: ${POST_RETIREMENT_VACATION_RATE}% p.a.`, variant: "rate" },
        { label: `Alloc: ${fmtINR(vacationBucket)}`, variant: "alloc" },
      ],
    },
    {
      title: "Family Legacy",
      dotColor: "#79FD06",
      value: fmtINR(legacyGrows),
      description: "Untouched wealth creation engine for inter-generational wealth.",
      chips: [
        { label: "Multi Cap", variant: "fund" },
        {
          label: `${retirementDuration}Y: ${PRE_RETIREMENT_RETURN}-${PRE_RETIREMENT_LONG_TENURE_RETURN}% p.a.`,
          variant: "rate",
        },
        { label: `Alloc: ${fmtINR(legacyBucket)}`, variant: "alloc" },
      ],
    },
  ];

  const renderCenterLabel = (chart: ChartWithLabel) => {
    chart.customLabel?.destroy();

    const centerX = chart.plotWidth / 2 + chart.plotLeft;
    const centerY = chart.plotHeight / 2 + chart.plotTop;

    const group = chart.renderer.g().add();

    chart.renderer
      .text(fmtINR(corpus), centerX, centerY - (isMobile ? 2 : 4))
      .attr({ align: "center", zIndex: 5 })
      .css({ color: "#ffffff", fontSize: isMobile ? "12px" : "16px", fontWeight: "700" })
      .add(group);

    chart.renderer
      .text("Corpus", centerX, centerY + (isMobile ? 12 : 18))
      .attr({ align: "center", zIndex: 5 })
      .css({ color: "#ffffff", fontSize: isMobile ? "10px" : "13px", fontWeight: "500" })
      .add(group);

    chart.customLabel = group;
  };

  const chartOptions: Highcharts.Options = {
    chart: {
      type: "pie",
      backgroundColor: "transparent",
      width: 120,
      height: 120,
      spacing: [0, 0, 0, 0],
      events: {
        load() {
          renderCenterLabel(this as ChartWithLabel);
        },
        redraw() {
          renderCenterLabel(this as ChartWithLabel);
        },
      },
    },
    title: { text: undefined },
    tooltip: { enabled: false },
    legend: { enabled: false },
    credits: { enabled: false },
    plotOptions: {
      pie: {
        enableMouseTracking: false,
        innerSize: "75%",
        shadow: false,
        borderWidth: 4,
        borderColor: "transparent",
        borderRadius: 4,
        dataLabels: { enabled: false },
      },
    },
    series: [
      {
        type: "pie",
        data: legendItems.map((item) => ({
          name: item.label,
          y: item.percent,
          color: item.dotColor,
        })),
      },
    ],
  };

  return (
    <div className="flex flex-col h-full">
      <RetirementStepsTitle stepTitle="Let's Understand your Retirement Scenario" className="mb-3" />

      <div className="flex flex-col sm:flex-row sm:bg-[#EDF4FF] sm:border border-primaryBlue sm:rounded-6xl flex-1 overflow-y-auto scrollbar-none sm:overflow-hidden">
        {/* Left: donut chart + legend */}
        <div className="w-full sm:w-[45%] shrink-0 sm:p-4.5">
          <div className="flex flex-col rounded-2xl md:rounded-6xl p-4 text-white sm:h-full bg-cover bg-center summary-step-financial sm:overflow-y-auto scrollbar-none">
            <div className="flex justify-center shrink-0">
              <HighchartsReact key={isMobile ? "mobile" : "desktop"} highcharts={Highcharts} options={chartOptions} />
            </div>

            <div className="flex flex-col gap-3 sm:gap-3.5 mt-4">
              {legendItems.map((item) => (
                <div key={item.label} className="flex items-start gap-1 text-2xs">
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full mt-1" style={{ backgroundColor: item.dotColor }} />

                  <div className="space-y-0.5 text-white">
                    <p className="font-medium leading-1.2">{item.label}</p>
                    <p className="font-bold leading-1.2">{item.percent}%</p>
                  </div>

                  <div className="ml-auto text-right shrink-0 text-white space-y-0.5">
                    <p className="font-bold leading-1.2">{fmtINR(item.value)}</p>
                    <p className="text-xs font-medium leading-1.2">
                      {item.prefix}{" "}
                      <span className={item.secondaryValue > 0 ? "text-[#22E100]" : "text-[#e10000]"}>
                        {fmtINR(item.secondaryValue)}
                      </span>{" "}
                      {item.suffix}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right: bucket breakdown */}
        <div className="flex flex-col gap-3 sm:gap-4 flex-1 mt-4 sm:mt-0 sm:p-4.5 sm:pl-0 sm:overflow-y-auto scrollbar-none">
          {details.map((item) => (
            <div key={item.title} className="rounded-1.5xl bg-[#EDF4FF] sm:bg-white p-3 xs:p-4">
              <div className="flex items-center justify-between gap-2 text-sm font-semibold">
                <p className="flex items-center gap-1.5 text-black">
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: item.dotColor }} />
                  {item.title}
                </p>

                <p className="text-[#003D6C] text-right whitespace-nowrap">{item.value}</p>
              </div>

              <p className="text-xs sm:text-2xs sm:leading-1.3 text-[#666666] mt-2">{item.description}</p>

              <div className="flex flex-wrap gap-1.5 mt-2.5">
                {item.chips.map((chip) => (
                  <span
                    key={chip.label}
                    className={[
                      "rounded-7xl px-2 py-1.5 text-xs leading-1.2 tracking-[-3%] whitespace-nowrap",
                      CHIP_STYLES[chip.variant],
                    ].join(" ")}
                  >
                    {chip.label}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <p className="text-[10px] text-black/40 text-center lg:text-left mt-2">
        <b>Disclaimer:</b> Calculations and projected fund returns are based on the historical performance of mutual
        funds. Actual investment returns depend on market conditions and may vary over time.
      </p>
    </div>
  );
}
